/**
 * runtime-registry.ts
 *
 * Internal Multi-Model Runtime Registry, Monotonic Index Allocation,
 * Hardware Memory Admission, Optimistic Revision Locking, and Safe Drain Semantics.
 *
 * Note: This module is internal to the runtime subsystem. External consumers
 * MUST use NeedleRuntimeService or LlmRuntimeService.
 */

import {
  ModelRuntime,
  ModelKind,
  CapacityError,
  PolicyError,
  AuthorizationError,
} from '../contracts/model-orchestration-contracts.js';
import { OrchestrationStore } from '../storage/orchestration-store.js';
import { MonotonicWorkerIndexAllocator } from './worker-index-allocator.js';
import { NodeCapabilityRegistry } from '../nodes/node-capability-registry.js';
import { ModelDefinitionRegistry } from '../catalog/model-definition-registry.js';

export class RuntimeRegistry {
  private readonly indexAllocator = new MonotonicWorkerIndexAllocator();

  constructor(
    private readonly store: OrchestrationStore,
    private readonly nodeRegistry: NodeCapabilityRegistry,
    private readonly modelRegistry: ModelDefinitionRegistry
  ) {}

  async getAllRuntimes(): Promise<readonly ModelRuntime[]> {
    return this.store.getRuntimes();
  }

  async getRuntimesForNode(nodeId: string, kind?: ModelKind): Promise<readonly ModelRuntime[]> {
    const runtimes = await this.store.getRuntimes();
    return runtimes.filter((r) => r.nodeId === nodeId && (!kind || r.kind === kind));
  }

  async getRuntime(runtimeId: string): Promise<ModelRuntime | undefined> {
    const runtimes = await this.store.getRuntimes();
    return runtimes.find((r) => r.runtimeId === runtimeId);
  }

  // ── Needle2 Elastic Worker Pool Operations ───────────────────────────────────

  async spawnNeedleInstance(nodeId: string, modelId: string): Promise<ModelRuntime> {
    const node = await this.nodeRegistry.getNode(nodeId);
    if (!node) {
      throw new PolicyError(`Node ${nodeId} is not registered`);
    }

    const model = await this.modelRegistry.getModel(modelId);
    if (!model) {
      throw new PolicyError(`Model ${modelId} not found in catalog`);
    }
    if (model.kind !== 'needle2') {
      throw new PolicyError(`Model ${modelId} is not a Needle2 model kind`);
    }

    // 1. Capacity & Policy Evaluation
    const activeNeedleWorkers = (await this.getRuntimesForNode(nodeId, 'needle2')).filter(
      (r) => r.status === 'active' || r.status === 'starting'
    );

    if (activeNeedleWorkers.length >= node.policy.maxNeedleInstances) {
      throw new CapacityError(
        `Node ${nodeId} has reached maximum Needle2 instances (${node.policy.maxNeedleInstances})`
      );
    }

    const totalProjectedMemoryMb =
      activeNeedleWorkers.reduce((sum, w) => sum + w.allocatedRamMb, 0) + model.estimatedRamMb;

    if (totalProjectedMemoryMb > node.capacity.availableMemoryMb) {
      throw new CapacityError(
        `Memory budget exceeded on ${nodeId}: projected ${totalProjectedMemoryMb}MB > available ${node.capacity.availableMemoryMb}MB`
      );
    }

    // 2. Monotonic Index & Generation
    const { instanceIndex, generation } = this.indexAllocator.allocateNext(nodeId, modelId);
    const runtimeId = `${nodeId}:needle2:${modelId}:i${instanceIndex}:g${generation}`;

    const runtime: ModelRuntime = {
      runtimeId,
      nodeId,
      modelId,
      kind: 'needle2',
      instanceIndex,
      generation,
      desiredState: 'running',
      status: 'active',
      allocatedRamMb: model.estimatedRamMb,
      allocatedVramMb: model.estimatedVramMb,
      maxConcurrentRequests: 8,
      activeRequests: 0,
      startedAt: new Date().toISOString(),
      lastActiveAt: new Date().toISOString(),
      protectedWorkload: false,
      drainTimeoutMs: 30000,
      revision: 1,
    };

    await this.store.saveRuntime(runtime);
    await this.store.appendEvent({
      eventId: `evt-spawn-${Date.now()}-${runtimeId}`,
      kind: 'runtime.started',
      payload: { runtimeId, nodeId, modelId, instanceIndex, generation },
      originId: 'runtime-registry',
      sequence: 0,
      occurredAt: new Date().toISOString(),
    });

    return runtime;
  }

  async scaleNeedlePool(
    nodeId: string,
    modelId: string,
    targetCount: number,
    poolRevisionNumber?: number
  ): Promise<void> {
    const currentPoolRev = await this.store.getPoolRevision(nodeId, modelId);
    const expectedRevision = poolRevisionNumber ?? currentPoolRev.revision;

    const currentWorkers = (await this.getRuntimesForNode(nodeId, 'needle2')).filter(
      (w) => w.status === 'active' || w.status === 'starting'
    );

    if (currentWorkers.length === targetCount) {
      return; // Already at target
    }

    if (targetCount > currentWorkers.length) {
      // Scale Up
      const toSpawnCount = targetCount - currentWorkers.length;
      const newWorkers: ModelRuntime[] = [];
      const model = await this.modelRegistry.getModel(modelId);
      const estimatedRam = model?.estimatedRamMb ?? 1200;

      for (let k = 0; k < toSpawnCount; k++) {
        const { instanceIndex, generation } = this.indexAllocator.allocateNext(nodeId, modelId);
        const runtimeId = `${nodeId}:needle2:${modelId}:i${instanceIndex}:g${generation}`;
        newWorkers.push({
          runtimeId,
          nodeId,
          modelId,
          kind: 'needle2',
          instanceIndex,
          generation,
          desiredState: 'running',
          status: 'active',
          allocatedRamMb: estimatedRam,
          allocatedVramMb: 0,
          maxConcurrentRequests: 8,
          activeRequests: 0,
          startedAt: new Date().toISOString(),
          lastActiveAt: new Date().toISOString(),
          protectedWorkload: false,
          drainTimeoutMs: 30000,
          revision: 1,
        });
      }

      await this.store.scaleRuntimes({
        upsert: newWorkers,
        deleteRuntimeIds: [],
        poolRevision: { nodeId, modelId, revision: expectedRevision },
      });
    } else {
      // Scale Down: Gracefully drain highest-index workers
      const toDrainCount = currentWorkers.length - targetCount;
      const sortedByDescIndex = [...currentWorkers].sort((a, b) => b.instanceIndex - a.instanceIndex);
      const drainingWorkers = sortedByDescIndex.slice(0, toDrainCount);

      const drainingUpdates: ModelRuntime[] = drainingWorkers.map((w) => ({
        ...w,
        desiredState: 'draining' as const,
        status: (w.activeRequests === 0 ? 'stopped' : 'draining') as ModelRuntime['status'],
      }));

      await this.store.scaleRuntimes({
        upsert: drainingUpdates,
        deleteRuntimeIds: [], // Keep visible in store until fully stopped
        poolRevision: { nodeId, modelId, revision: expectedRevision },
      });
    }

    await this.store.appendEvent({
      eventId: `evt-scaled-${Date.now()}`,
      kind: 'runtime.scaled',
      payload: { nodeId, modelId, previousCount: currentWorkers.length, targetCount },
      originId: 'runtime-registry',
      sequence: 0,
      occurredAt: new Date().toISOString(),
    });
  }

  // ── LLM Discrete Runtime Operations ──────────────────────────────────────────

  async startLlmRuntime(nodeId: string, modelId: string): Promise<ModelRuntime> {
    const node = await this.nodeRegistry.getNode(nodeId);
    if (!node) {
      throw new PolicyError(`Node ${nodeId} is not registered`);
    }

    const model = await this.modelRegistry.getModel(modelId);
    if (!model) {
      throw new PolicyError(`Model ${modelId} not found in catalog`);
    }
    if (model.kind !== 'llm') {
      throw new PolicyError(`Model ${modelId} is not an LLM model kind`);
    }

    const currentLlms = (await this.getRuntimesForNode(nodeId, 'llm')).filter(
      (r) => r.status === 'active' || r.status === 'starting'
    );

    // RT-06: Idempotent return if same model already running
    const existingActive = currentLlms.find((r) => r.modelId === modelId);
    if (existingActive) {
      return existingActive;
    }

    // Check multiplicity limit
    if (currentLlms.length >= node.policy.maxActiveLlmRuntimes) {
      throw new CapacityError(
        `Node ${nodeId} has reached maximum active LLM runtimes (${node.policy.maxActiveLlmRuntimes})`
      );
    }

    if (!node.policy.allowMixedLlmModels && currentLlms.length > 0) {
      throw new PolicyError(`Node ${nodeId} does not allow mixed LLM models simultaneously`);
    }

    const runtimeId = `${nodeId}:llm:${modelId}:i0:g0`;
    const runtime: ModelRuntime = {
      runtimeId,
      nodeId,
      modelId,
      kind: 'llm',
      instanceIndex: 0,
      generation: 0,
      desiredState: 'running',
      status: 'active',
      allocatedRamMb: model.estimatedRamMb,
      allocatedVramMb: model.estimatedVramMb,
      maxConcurrentRequests: 4,
      activeRequests: 0,
      startedAt: new Date().toISOString(),
      lastActiveAt: new Date().toISOString(),
      protectedWorkload: false,
      drainTimeoutMs: 60000,
      revision: 1,
    };

    await this.store.saveRuntime(runtime);
    await this.store.appendEvent({
      eventId: `evt-llm-start-${Date.now()}`,
      kind: 'runtime.started',
      payload: { runtimeId, nodeId, modelId, kind: 'llm' },
      originId: 'runtime-registry',
      sequence: 0,
      occurredAt: new Date().toISOString(),
    });

    return runtime;
  }

  // ── Universal Stop & Teardown ────────────────────────────────────────────────

  async stopRuntime(runtimeId: string, mode: 'drain' | 'terminate' | 'force_kill' = 'drain'): Promise<void> {
    const runtime = await this.getRuntime(runtimeId);
    if (!runtime) return;

    if (runtime.protectedWorkload && mode === 'force_kill') {
      throw new AuthorizationError('Cannot force kill protected workload without step-up auth');
    }

    const nextStatus = mode === 'drain' && runtime.activeRequests > 0 ? 'draining' : 'stopped';
    const updated: ModelRuntime = {
      ...runtime,
      desiredState: mode === 'drain' && nextStatus === 'draining' ? 'draining' : 'stopped',
      status: nextStatus,
    };

    await this.store.saveRuntime(updated);
    await this.store.appendEvent({
      eventId: `evt-stop-${Date.now()}-${runtimeId}`,
      kind: mode === 'force_kill' ? 'runtime.force_killed' : 'runtime.stopped',
      payload: { runtimeId, mode, previousStatus: runtime.status, nextStatus },
      originId: 'runtime-registry',
      sequence: 0,
      occurredAt: new Date().toISOString(),
    });
  }
}
